// AI Skills Service (Frontend)
// Handles API calls for AI skill management (org-scoped + admin global)

import { supabase } from '../utils/supabaseClient';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

export type AiSkill = {
  id: string;
  tenant_id: string | null;
  created_by: string | null;
  is_personal: boolean;
  skill_name: string;
  system_prompt_md: string;
  default_flag: boolean;
  created_at: string;
  updated_at: string;
  organisation_ids: string[];
};

type CreateSkillPayload = {
  skill_name: string;
  system_prompt_md: string;
  default_flag?: boolean;
  is_personal?: boolean;
  organisation_ids?: string[];
};

type UpdateSkillPayload = {
  skill_name?: string;
  system_prompt_md?: string;
  default_flag?: boolean;
  is_personal?: boolean;
  organisation_ids?: string[];
};

const getAuthHeaders = async (): Promise<HeadersInit> => {
  const { data: { session } } = await supabase.auth.getSession();

  if (!session?.access_token) {
    throw new Error('No active session');
  }

  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${session.access_token}`,
  };
};

// =====================================================
// ORG-SCOPED SKILL CRUD (existing /api/charlie/ai-skills)
// =====================================================

// Fetch all AI skills (global + org-scoped)
export const getAiSkills = async (): Promise<AiSkill[]> => {
  const headers = await getAuthHeaders();

  const response = await fetch(`${API_BASE_URL}/api/charlie/ai-skills`, { headers });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to fetch AI skills');
  }

  const { skills } = await response.json();
  return skills as AiSkill[];
};

// Create a new org-scoped AI skill
export const createAiSkill = async (payload: CreateSkillPayload): Promise<AiSkill> => {
  const headers = await getAuthHeaders();

  const response = await fetch(`${API_BASE_URL}/api/charlie/ai-skills`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to create AI skill');
  }

  const { skill } = await response.json();
  return skill as AiSkill;
};

// Update an existing org-scoped AI skill
export const updateAiSkill = async (id: string, payload: UpdateSkillPayload): Promise<AiSkill> => {
  const headers = await getAuthHeaders();

  const response = await fetch(`${API_BASE_URL}/api/charlie/ai-skills/${id}`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to update AI skill');
  }

  const { skill } = await response.json();
  return skill as AiSkill;
};

// Delete an org-scoped AI skill
export const deleteAiSkill = async (id: string): Promise<void> => {
  const headers = await getAuthHeaders();

  const response = await fetch(`${API_BASE_URL}/api/charlie/ai-skills/${id}`, {
    method: 'DELETE',
    headers,
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to delete AI skill');
  }
};

// =====================================================
// ADMIN GLOBAL SKILL CRUD (/api/admin/skills)
// =====================================================

// Fetch all global skills (platform admin only)
export const getGlobalSkills = async (): Promise<AiSkill[]> => {
  const headers = await getAuthHeaders();

  const response = await fetch(`${API_BASE_URL}/api/admin/skills`, { headers });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to fetch global skills');
  }

  const { data } = await response.json();
  return (data as AiSkill[]).map((s) => ({ ...s, organisation_ids: s.organisation_ids ?? [] }));
};

// Create a global skill (platform admin only)
export const createGlobalSkill = async (payload: { skill_name: string; system_prompt_md: string; default_flag?: boolean }): Promise<AiSkill> => {
  const headers = await getAuthHeaders();

  const response = await fetch(`${API_BASE_URL}/api/admin/skills`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to create global skill');
  }

  const { data } = await response.json();
  return { ...data, organisation_ids: [] } as AiSkill;
};

// Update a global skill (platform admin only)
export const updateGlobalSkill = async (id: string, payload: { skill_name?: string; system_prompt_md?: string; default_flag?: boolean }): Promise<AiSkill> => {
  const headers = await getAuthHeaders();

  const response = await fetch(`${API_BASE_URL}/api/admin/skills/${id}`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to update global skill');
  }

  const { data } = await response.json();
  return { ...data, organisation_ids: [] } as AiSkill;
};

// Delete a global skill (platform admin only)
export const deleteGlobalSkill = async (id: string): Promise<void> => {
  const headers = await getAuthHeaders();

  const response = await fetch(`${API_BASE_URL}/api/admin/skills/${id}`, {
    method: 'DELETE',
    headers,
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to delete global skill');
  }
};

// =====================================================
// SKILL-TO-PROVIDER ASSIGNMENTS
// =====================================================

export type SkillAssignment = {
  id: string;
  tenant_id: string;
  provider: 'knowledge-base' | 'certant-ai';
  skill_id: string | null;
  skill: AiSkill | null;
  created_at: string;
  updated_at: string;
};

// Fetch all skill-provider assignments for the current org
export const getSkillAssignments = async (): Promise<SkillAssignment[]> => {
  const headers = await getAuthHeaders();

  const response = await fetch(`${API_BASE_URL}/api/charlie/ai-skill-assignments`, { headers });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to fetch skill assignments');
  }

  const { data } = await response.json();
  return data as SkillAssignment[];
};

// Upsert a skill assignment for a provider
export const upsertSkillAssignment = async (
  provider: 'knowledge-base' | 'certant-ai',
  skillId: string | null
): Promise<SkillAssignment | null> => {
  const headers = await getAuthHeaders();

  const response = await fetch(`${API_BASE_URL}/api/charlie/ai-skill-assignments`, {
    method: 'PUT',
    headers,
    body: JSON.stringify({ provider, skillId }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to update skill assignment');
  }

  const { data } = await response.json();
  return data as SkillAssignment | null;
};

// Remove a skill assignment for a provider
export const deleteSkillAssignment = async (
  provider: 'knowledge-base' | 'certant-ai'
): Promise<void> => {
  const headers = await getAuthHeaders();

  const response = await fetch(`${API_BASE_URL}/api/charlie/ai-skill-assignments/${provider}`, {
    method: 'DELETE',
    headers,
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to delete skill assignment');
  }
};

// =====================================================
// GLOBAL SKILL-TO-PROVIDER ASSIGNMENTS (admin only)
// These set the default skill for ALL orgs
// =====================================================

// Fetch all global skill-provider assignments
export const getGlobalSkillAssignments = async (): Promise<SkillAssignment[]> => {
  const headers = await getAuthHeaders();

  const response = await fetch(`${API_BASE_URL}/api/admin/skill-assignments`, { headers });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to fetch global skill assignments');
  }

  const { data } = await response.json();
  return data as SkillAssignment[];
};

// Upsert a global skill assignment for a provider (admin only)
export const upsertGlobalSkillAssignment = async (
  provider: 'knowledge-base' | 'certant-ai',
  skillId: string | null
): Promise<SkillAssignment | null> => {
  const headers = await getAuthHeaders();

  const response = await fetch(`${API_BASE_URL}/api/admin/skill-assignments`, {
    method: 'PUT',
    headers,
    body: JSON.stringify({ provider, skillId }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to update global skill assignment');
  }

  const { data } = await response.json();
  return data as SkillAssignment | null;
};

// Remove a global skill assignment for a provider (admin only)
export const deleteGlobalSkillAssignment = async (
  provider: 'knowledge-base' | 'certant-ai'
): Promise<void> => {
  const headers = await getAuthHeaders();

  const response = await fetch(`${API_BASE_URL}/api/admin/skill-assignments/${provider}`, {
    method: 'DELETE',
    headers,
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to delete global skill assignment');
  }
};
